import { motion } from "framer-motion";
import { ABOUT, THUMBNAILS, PROFILE } from "@/data/content";

const TAPE = ["bg-yellow", "bg-coral", "bg-white"];

export const About = () => {
  return (
    <section id="about" data-testid="about" className="py-20 md:py-32 px-5 md:px-10 overflow-hidden">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          {/* Story copy */}
          <div>
            <span className="font-outfit text-xs uppercase tracking-widest text-coral">{ABOUT.overline}</span>
            <h2 className="font-anton uppercase text-4xl md:text-6xl leading-[0.95] text-yellow mt-4">
              {ABOUT.heading}
            </h2>
            <div className="mt-7 space-y-5">
              {ABOUT.paragraphs.map((p, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className={`font-outfit font-medium leading-relaxed text-base md:text-lg ${
                    i === ABOUT.paragraphs.length - 1 ? "text-white" : "text-yellow"
                  }`}
                >
                  {p}
                </motion.p>
              ))}
            </div>
          </div>

          {/* Nostalgic polaroid cluster */}
          <div className="relative">
            <motion.p
              initial={{ opacity: 0, rotate: -8, y: 10 }}
              whileInView={{ opacity: 1, rotate: -4, y: 0 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 150, damping: 12 }}
              className="font-hand text-4xl md:text-5xl text-white text-center mb-8"
            >
              I was cuter back then lol...
            </motion.p>

            <div className="flex flex-wrap justify-center items-center gap-2 md:gap-4">
              {ABOUT.photos.map((ph, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 50, rotate: ph.rotate * 2.5, scale: 0.85 }}
                  whileInView={{ opacity: 1, y: 0, rotate: ph.rotate, scale: 1 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ type: "spring", stiffness: 90, damping: 12, delay: i * 0.12 }}
                  whileHover={{ rotate: 0, scale: 1.05, zIndex: 20 }}
                  className={`relative bg-white p-2.5 pb-8 shadow-2xl ${i === 1 ? "-mt-4 md:-mt-8" : ""}`}
                  style={{ width: i === 1 ? "44%" : "40%" }}
                >
                  <span
                    className={`absolute -top-3 left-1/2 -translate-x-1/2 h-6 w-16 rotate-2 opacity-80 ${TAPE[i % TAPE.length]}`}
                  />
                  <img
                    src={ph.src}
                    alt={ph.alt}
                    loading="lazy"
                    className="w-full aspect-square object-cover"
                    style={{ filter: "sepia(0.35) saturate(1.05) contrast(0.96)" }}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Then vs now — example thumbnails */}
        <div className="mt-20 md:mt-28">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="font-anton uppercase text-2xl md:text-4xl text-white text-center mb-8"
          >
            ...and here&apos;s what I&apos;m making now <span className="text-yellow">👇</span>
          </motion.p>
          <div className="grid sm:grid-cols-3 gap-4 md:gap-6">
            {THUMBNAILS.map((t, i) => (
              <motion.a
                key={i}
                href={PROFILE.youtube}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`thumbnail-${i}`}
                initial={{ opacity: 0, y: 40, rotate: i % 2 ? 2 : -2 }}
                whileInView={{ opacity: 1, y: 0, rotate: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ type: "spring", stiffness: 110, damping: 13, delay: i * 0.1 }}
                whileHover={{ y: -8, scale: 1.03 }}
                className="block rounded-2xl overflow-hidden border-4 border-white shadow-2xl aspect-video bg-obsidian"
              >
                <img src={t.src} alt={t.alt} loading="lazy" className="h-full w-full object-cover" />
              </motion.a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
